import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Copy, Download, Printer, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatBackupCode } from '@/utils/mfaHelpers';

interface BackupCodesDisplayProps {
  codes: string[];
}

export const BackupCodesDisplay = ({ codes }: BackupCodesDisplayProps) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { toast } = useToast();

  const copyCode = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    
    toast({
      title: 'Kopiert',
      description: 'Code wurde in die Zwischenablage kopiert'
    });
  };

  const copyAllCodes = () => {
    const allCodes = codes.map(formatBackupCode).join('\n');
    navigator.clipboard.writeText(allCodes);
    
    toast({
      title: 'Alle Codes kopiert',
      description: 'Alle Backup-Codes wurden in die Zwischenablage kopiert'
    });
  };

  const downloadCodes = () => {
    const content = `MediQR Backup-Codes\n\nBewahren Sie diese Codes sicher auf!\n\n${codes.map(formatBackupCode).join('\n')}\n\nJeder Code kann nur einmal verwendet werden.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mediqr-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Download gestartet',
      description: 'Backup-Codes wurden heruntergeladen'
    });
  };

  const printCodes = () => {
    const content = `
      <html>
        <head>
          <title>MediQR Backup-Codes</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #0072C6; }
            .warning { color: #dc2626; font-weight: bold; margin: 20px 0; }
            .codes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
            .code { font-family: monospace; font-size: 18px; padding: 10px; border: 1px solid #ddd; }
          </style>
        </head>
        <body>
          <h1>MediQR Backup-Codes</h1>
          <p class="warning">⚠️ Bewahren Sie diese Codes sicher auf! Jeder Code kann nur einmal verwendet werden.</p>
          <div class="codes">
            ${codes.map(code => `<div class="code">${formatBackupCode(code)}</div>`).join('')}
          </div>
          <p>Generiert am: ${new Date().toLocaleDateString('de-DE')}</p>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Card className="p-6 backdrop-blur-md bg-background/60 border border-primary/20">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Backup-Codes</h3>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyAllCodes}
              className="gap-2"
            >
              <Copy className="w-4 h-4" />
              Alle kopieren
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCodes}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={printCodes}
              className="gap-2"
            >
              <Printer className="w-4 h-4" />
              Drucken
            </Button>
          </div>
        </div>

        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-sm text-destructive">
            ⚠️ Bewahren Sie diese Codes sicher auf! Jeder Code kann nur einmal verwendet werden.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {codes.map((code, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50 hover:border-primary/30 transition-colors"
            >
              <code className="text-sm font-mono">{formatBackupCode(code)}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyCode(code, index)}
                className="ml-2"
              >
                {copiedIndex === index ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
